import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RETURN_TO_KEY } from '@/components/auth0-provider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ChooseLogo } from '@/components/ui/choose-logo';
import { getAuth0AuthorizationParams } from '@/lib/config';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2, LogIn } from 'lucide-react';

export function Login() {
  const { t, i18n } = useTranslation();
  const { loginWithRedirect } = useAuth0();
  const [isI18nReady, setIsI18nReady] = useState(false);

  useEffect(() => {
    // Wait for i18next to be fully initialized
    if (i18n.isInitialized) {
      setIsI18nReady(true);
    } else {
      i18n.on('initialized', () => {
        setIsI18nReady(true);
      });
    }
  }, [i18n]);

  useEffect(() => {
    document.title = 'Connexion - Smart Offers';
  }, []);

  const handleLogin = () => {
    const authorizationParams = getAuth0AuthorizationParams();

    // Get the current path (without domain) to redirect back after login
    const currentPath =
      window.location.pathname + window.location.search + window.location.hash;

    // Check if we already have a stored returnTo (from auth-wrapper)
    const storedReturnTo = localStorage.getItem(RETURN_TO_KEY);

    // Determine the return path - use stored value first, then current path
    let returnTo = storedReturnTo || currentPath;

    // Avoid redirecting to special pages
    if (['/login', '/redirect', '/logout', '/'].includes(returnTo)) {
      returnTo = '/';
    }

    // Store in localStorage as backup (in case appState doesn't work)
    localStorage.setItem(RETURN_TO_KEY, returnTo);

    // Also pass via appState for proper Auth0 flow
    loginWithRedirect({
      authorizationParams: {
        ...authorizationParams,
        // Uncomment to force passwordless email authentication:
        // connection: 'email',
      },
      appState: {
        returnTo,
      },
    });
  };

  // Show loading state while i18n initializes (prevents hydration mismatch)
  if (!isI18nReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <ChooseLogo width={30} height={30} />
          </div>
          <div>
            <CardTitle className="text-2xl">
              {t('components.login.title')}
            </CardTitle>
            <CardDescription className="mt-2 text-base">
              {t('components.login.message')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <Button
            onClick={handleLogin}
            data-testid="login-button"
            className="h-12 w-full text-base"
            size="lg"
          >
            <LogIn className="mr-2 h-5 w-5" />
            {t('components.login.button')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
